import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import canonicaltemplateService from '../services/canonicaltemplateService';
import canonicalgroupService from '../services/canonicalgroupService';
import vaultsourcegroupService from '../services/vaultsourcegroupService';
import vaultsqlService from '../services/vaultsqlService';
import { useAccount } from '../contexts/AccountContext';
import CountrySelect from './CountrySelect';
import './CanonicalTemplateEditor.css';

/**
 * Extract SELECT column aliases/names from a SQL string.
 * Handles "expr AS alias", "table.column", bare column names.
 */
function extractSqlColumns(sql) {
  if (!sql) return [];
  const match = sql.match(/SELECT\s+([\s\S]+?)\s+FROM\b/i);
  if (!match) return [];
  const colsPart = match[1];
  // Split by commas, respecting parentheses depth
  const cols = [];
  let depth = 0, current = '';
  for (const ch of colsPart) {
    if (ch === '(') { depth++; current += ch; }
    else if (ch === ')') { depth--; current += ch; }
    else if (ch === ',' && depth === 0) { cols.push(current.trim()); current = ''; }
    else { current += ch; }
  }
  if (current.trim()) cols.push(current.trim());

  return cols.map(col => {
    const asMatch = col.match(/\bAS\s+`?(\w+)`?\s*$/i);
    if (asMatch) return asMatch[1];
    const parts = col.trim().split(/[\s.]+/);
    return parts[parts.length - 1].replace(/[`'"]/g, '') || null;
  }).filter(c => c && c !== '*');
}

function getNestedValue(obj, path) {
  return path.trim().split('.').reduce((acc, key) => {
    if (acc === null || acc === undefined) return null;
    const i = parseInt(key);
    return (!isNaN(i) && Array.isArray(acc)) ? acc[i] : acc?.[key];
  }, obj);
}

/**
 * Flatten a nested object into dot-notation keys.
 * e.g. { a: { b: 1 }, c: 2 } → ['a.b', 'c']
 */
function flattenKeys(obj, prefix = '') {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return prefix ? [prefix] : [];
  return Object.keys(obj).flatMap((key) => {
    const full = prefix ? `${prefix}.${key}` : key;
    return typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])
      ? flattenKeys(obj[key], full)
      : [full];
  });
}

/**
 * Flatten a nested object into a key→value map using dot-notation.
 * Arrays are indexed numerically (items.0.name, items.1.name, …).
 */
function flattenObject(obj, prefix = '') {
  const result = {};
  for (const [key, value] of Object.entries(obj || {})) {
    const full = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(value)) {
      value.forEach((item, i) => {
        if (item !== null && typeof item === 'object') {
          Object.assign(result, flattenObject(item, `${full}.${i}`));
        } else {
          result[`${full}.${i}`] = item != null ? String(item) : '';
        }
      });
    } else if (value !== null && typeof value === 'object') {
      Object.assign(result, flattenObject(value, full));
    } else {
      result[full] = value != null ? String(value) : '';
    }
  }
  return result;
}

// Standard vault Athena table columns — always available in vault templates
const VAULT_STANDARD_FIELDS = [
  'invoiceNo', 'folioNo', 'invoiceDate', 'billingName', 'guestName',
  'departureDate', 'arrivalDate', 'email', 'confirmationNumber',
  'invoiceAmount', 'membershipNumber', 'externalReference', 'arNo',
  'year', 'month'
];

// Hotel fields from the hotel table
const HOTEL_FIELDS = [
  'hotel.hotelname', 'hotel.hotelcode',
  'hotel.ADDRESS1', 'hotel.ADDRESS2', 'hotel.CITY', 'hotel.STATE', 'hotel.ZIPCODE',
  'hotel.PHONE', 'hotel.FAX', 'hotel.WEBSITE',
  'hotel.COUNTRY', 'hotel.CURRENCY'
];

function CanonicalTemplateEditor({ mode = 'create' }) {
  const navigate = useNavigate();
  const { canonicalid, groupid, id } = useParams();
  const { accountData } = useAccount();

  const editorRef = useRef(null);
  const quillRef = useRef(null);

  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [payloadKeys, setPayloadKeys] = useState([]);
  const [sqlFields, setSqlFields] = useState([]);
  const [groupPayload, setGroupPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [initialContent, setInitialContent] = useState('');
  const [dataReady, setDataReady] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlSource, setHtmlSource] = useState('');
  const [showPreview, setShowPreview] = useState(null);
  const [fieldSearch, setFieldSearch] = useState('');

  // Load canonical group (for payload keys + name) and template (if editing)
  useEffect(() => {
    async function loadData() {
      try {
        // Load canonical group for payload keys
        const groupRes = await canonicalgroupService.getCanonicalGroupById(groupid);
        const group = groupRes.data;
        setGroupName(group.name || '');

        if (group.payload) {
          try {
            const payload = typeof group.payload === 'string'
              ? JSON.parse(group.payload)
              : group.payload;
            setPayloadKeys(flattenKeys(payload));
            setGroupPayload(payload);
          } catch (e) {
            console.error('Failed to parse payload:', e);
          }
        }

        // Find vault source group with matching name and load its SQL columns
        try {
          const vsgRes = await vaultsourcegroupService.getAllVaultSourceGroups({ name: group.name });
          const vsGroups = vsgRes.vaultsourcegroups || [];
          console.log('[fields] canonical group name:', group.name, '→ vault source groups:', vsGroups.length, vsGroups.map(g => g.name));
          if (vsGroups.length > 0) {
            const sqlRes = await vaultsqlService.getAllVaultSqls({ vaultsourcegroupid: vsGroups[0].id });
            const sqls = sqlRes.vaultsqls || [];
            console.log('[fields] vaultsqls found:', sqls.length, sqls.map(s => ({ name: s.name, hasSource: !!s.sql_source, preview: s.sql_source?.substring(0, 80) })));
            const allCols = sqls.flatMap(s => extractSqlColumns(s.sql_source));
            console.log('[fields] extracted columns:', allCols);
            const unique = [...new Set(allCols)];
            setSqlFields(unique);
          }
        } catch (e) {
          console.error('[fields] Failed to load SQL fields:', e);
        }

        // Load template content if editing
        if (mode === 'edit' && id) {
          const templateRes = await canonicaltemplateService.getById(id);
          const template = templateRes.data;
          setName(template.name || '');
          setCountry(template.country || '');
          setInitialContent(template.content || '');
          setHtmlSource(template.content || '');
        }
      } catch (err) {
        setError(err.response?.data?.error?.message || err.message || 'Failed to load data');
      } finally {
        setLoading(false);
        setDataReady(true);
      }
    }

    loadData();
  }, [groupid, mode, id]);

  // Initialize Quill once data is ready and editorRef is mounted
  useEffect(() => {
    if (!dataReady || !editorRef.current || quillRef.current) return;

    const quill = new Quill(editorRef.current, {
      theme: 'snow',
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, 4, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          ['link', 'image'],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          ['blockquote', 'code-block'],
          ['clean'],
        ],
      },
    });

    if (initialContent) {
      quill.root.innerHTML = initialContent;
    }

    quillRef.current = quill;
  }, [dataReady, initialContent]);

  const toggleHtmlMode = () => {
    if (!htmlMode) {
      // Entering HTML mode — if htmlSource is already set (loaded from DB), keep it;
      // otherwise snapshot current Quill content
      if (!htmlSource) {
        setHtmlSource(quillRef.current ? quillRef.current.root.innerHTML : '');
      }
    } else {
      // Leaving HTML mode — apply textarea content back to Quill
      if (quillRef.current) {
        quillRef.current.root.innerHTML = htmlSource;
      }
    }
    setHtmlMode(prev => !prev);
  };

  const insertVariable = (key) => {
    if (htmlMode) {
      setHtmlSource(prev => prev + `{{${key}}}`);
      return;
    }
    if (!quillRef.current) return;
    const range = quillRef.current.getSelection(true);
    quillRef.current.insertText(range ? range.index : 0, `{{${key}}}`);
    quillRef.current.focus();
  };

  const decodeHtml = (html) => {
    const ta = document.createElement('textarea');
    ta.innerHTML = html;
    return ta.value;
  };

  const handlePreview = () => {
    // Prefer htmlSource (raw HTML) — Quill sanitizes content on load and loses styles/tables
    const raw = htmlSource || (quillRef.current ? quillRef.current.root.innerHTML : '');
    const content = decodeHtml(raw);
    if (!content) return;
    const payload = groupPayload || {};
    const flat = flattenObject(payload);

    // Process {{#each path}}...{{/each}}
    let rendered = content.replace(/\{\{#each ([^}]+)\}\}([\s\S]*?)\{\{\/each\}\}/g, (match, path, itemTpl) => {
      const arr = getNestedValue(payload, path.trim());
      if (!Array.isArray(arr) || arr.length === 0) return '';
      return arr.map(item => {
        const itemFlat = flattenObject(item);
        return itemTpl.replace(/\{\{([^}]+)\}\}/g, (m, key) => {
          const t = key.trim();
          return itemFlat[t] ?? String(getNestedValue(item, t) ?? '');
        });
      }).join('');
    });

    // Process {{#if key}}...{{/if}}
    rendered = rendered.replace(/\{\{#if ([^}]+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (match, key, inner) => {
      const val = flat[key.trim()];
      return (val !== undefined && val !== null && val !== '') ? inner : '';
    });

    // Replace {{account.logo}} with the raw data URL so <img src="{{account.logo}}"> renders correctly
    rendered = rendered.replace(/\{\{account\.logo\}\}/g, accountData?.logo || '');

    // Replace {{key}} variables
    rendered = rendered.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
      const trimmed = key.trim();
      return flat[trimmed] !== undefined ? flat[trimmed] : '';
    });

    setShowPreview(rendered);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const content = htmlMode ? htmlSource : (quillRef.current ? quillRef.current.root.innerHTML : '');
      const data = {
        canonical_group_id: parseInt(groupid),
        name: name.trim(),
        country: country || null,
        content,
      };

      if (mode === 'create') {
        await canonicaltemplateService.create(data);
      } else {
        await canonicaltemplateService.update(id, data);
      }

      navigate(`/admin/canonical/${canonicalid}/canonicalgroup/${groupid}/edit`);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to save template');
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="template-editor-container">Loading...</div>;
  }

  return (
    <div className="template-editor-container">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/admin/canonical" className="breadcrumb-link">Canonicals</Link>
        <span className="breadcrumb-separator">/</span>
        <Link to={`/admin/canonical/${canonicalid}/canonicalgroup/${groupid}/edit`} className="breadcrumb-link">
          {groupName || 'Group'}
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">{mode === 'create' ? 'New Template' : 'Edit Template'}</span>
      </div>

      <h2>{mode === 'create' ? 'New Template' : 'Edit Template'}</h2>

      {error && <div className="error-message">{error}</div>}

      <div className="template-name-row">
        <label htmlFor="template-name">Name *</label>
        <input
          id="template-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Template name"
          className="template-name-input"
        />
        <label htmlFor="template-country" style={{ marginLeft: '1rem' }}>Country</label>
        <CountrySelect
          name="country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="Any country"
        />
      </div>

      <div className="template-editor-layout">
        {/* Payload variables panel */}
        <div className="template-payload-panel">
          <h4>Available Fields</h4>
          <input
            type="text"
            className="payload-field-search"
            placeholder="Search fields..."
            value={fieldSearch}
            onChange={e => setFieldSearch(e.target.value)}
          />
          <div className="payload-fields-list">
            {(() => {
              const q = fieldSearch.toLowerCase();
              const match = key => !q || key.toLowerCase().includes(q);
              const stdFields = VAULT_STANDARD_FIELDS.filter(match);
              const hotelFields = HOTEL_FIELDS.filter(match);
              const sqlExtra = sqlFields.filter(f => !VAULT_STANDARD_FIELDS.map(s => s.toLowerCase()).includes(f.toLowerCase()) && match(f));
              const payloadFiltered = payloadKeys.filter(match);
              return (
                <>
                  {match('account.logo') && (
                    <>
                      <div className="payload-fields-divider">Account</div>
                      <button type="button" className="payload-field-btn payload-field-btn--account" onClick={() => insertVariable('account.logo')} title="Insert account logo">
                        {'{{account.logo}}'}
                        {accountData?.logo && <img src={accountData.logo} alt="" style={{ maxHeight: 16, maxWidth: 40, marginLeft: 6, verticalAlign: 'middle', borderRadius: 2 }} />}
                      </button>
                    </>
                  )}
                  {stdFields.map((key) => (
                    <button key={key} type="button" className="payload-field-btn" onClick={() => insertVariable(key)} title={`Insert {{${key}}}`}>
                      {`{{${key}}}`}
                    </button>
                  ))}
                  {hotelFields.length > 0 && (
                    <>
                      <div className="payload-fields-divider">Hotel</div>
                      {hotelFields.map((key) => (
                        <button key={key} type="button" className="payload-field-btn payload-field-btn--hotel" onClick={() => insertVariable(key)} title={`Insert {{${key}}}`}>
                          {`{{${key}}}`}
                        </button>
                      ))}
                    </>
                  )}
                  {sqlExtra.length > 0 && (
                    <>
                      <div className="payload-fields-divider">SQL Columns</div>
                      {sqlExtra.map((key) => (
                        <button key={key} type="button" className="payload-field-btn" onClick={() => insertVariable(key)} title={`Insert {{${key}}}`}>
                          {`{{${key}}}`}
                        </button>
                      ))}
                    </>
                  )}
                  {payloadFiltered.length > 0 && (
                    <>
                      <div className="payload-fields-divider">Payload</div>
                      {payloadFiltered.map((key) => (
                        <button
                    key={key}
                    type="button"
                    className="payload-field-btn payload-field-btn--payload"
                    onClick={() => insertVariable(key)}
                    title={`Insert {{${key}}}`}
                  >
                    {`{{${key}}}`}
                  </button>
                ))}
              </>
            )}
          </>
        )
      })()}
          </div>
        </div>

        {/* Quill editor */}
        <div className="template-quill-wrap">
          <div className="template-quill-toolbar-extra">
            <button
              type="button"
              className={`btn-html-source${htmlMode ? ' active' : ''}`}
              onClick={toggleHtmlMode}
              title="Toggle HTML source editor"
            >
              {htmlMode ? 'Rich Text' : '&lt;/&gt; HTML Source'}
            </button>
          </div>
          {htmlMode ? (
            <textarea
              className="template-html-source"
              value={htmlSource}
              onChange={(e) => setHtmlSource(e.target.value)}
              spellCheck={false}
              placeholder="Paste raw HTML here..."
            />
          ) : (
            <div ref={editorRef} className="template-quill-editor" />
          )}
        </div>
      </div>

      <div className="template-editor-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : mode === 'create' ? 'Create Template' : 'Update Template'}
        </button>
        <button
          type="button"
          className="btn-preview"
          onClick={handlePreview}
          disabled={!groupPayload}
          title={!groupPayload ? 'No payload defined on this group' : 'Preview with example payload'}
        >
          Preview
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate(`/admin/canonical/${canonicalid}/canonicalgroup/${groupid}/edit`)}
          disabled={saving}
        >
          Cancel
        </button>
      </div>

      {showPreview && (
        <div className="template-preview-overlay" onClick={() => setShowPreview(null)}>
          <div className="template-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="template-preview-header">
              <span className="template-preview-title">Preview — {name || 'Template'}</span>
              <button type="button" className="template-preview-close" onClick={() => setShowPreview(null)}>✕ Close</button>
            </div>
            <iframe
              className="template-preview-body"
              srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;padding:0;}</style></head><body>${showPreview}</body></html>`}
              title="Template Preview"
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default CanonicalTemplateEditor;
